import React, { useMemo } from 'react';
import { TROPHIES } from '../data/realms';
import { useUser } from '../context/UserContext';
import {
  getSolvedSet,
  loadXpEvents,
  lastNDayXp,
  type XpEvent,
} from '../lib/progress';
import { type TrophyInputs } from '../data/trophies';
import { findProblemById } from '../data/problems';
import ProfileBody, {
  buildRealmStats,
  evaluateTrophyEarned,
  type RecentSolve,
} from '../components/ProfileBody';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import AccountManagement from './AccountManagement';

// Thin wrapper — delegates rendering to ProfileBody. All sourcing happens here:
// useUser() supplies backend-synced totals; lib/progress.ts supplies the local
// XP-events log that drives the heatmap / sparkline / recent solves until the
// Phase B backend swap (HLD §7 by_user_solved_at GSI).

const ProfilePage: React.FC = () => {
  const {
    username, level, xp, xpToNextLevel, xpPercent, coins, streak, solvedCount,
    solvedByTopic, solvedByTier, friendCount,
  } = useUser();
  useDocumentTitle(`@${username}`);

  const xpEvents: XpEvent[] = useMemo(() => loadXpEvents(), []);
  const solvedSet = useMemo(() => getSolvedSet(), []);
  const realmStats = useMemo(() => buildRealmStats(solvedByTopic), [solvedByTopic]);

  const heatDays = useMemo(() => lastNDayXp(xpEvents, 84), [xpEvents]);
  const sparkDays = useMemo(() => lastNDayXp(xpEvents, 7), [xpEvents]);

  const recentSolves: RecentSolve[] = useMemo(() => {
    return [...xpEvents]
      .sort((a, b) => b.ts - a.ts)
      .slice(0, 10)
      .map(e => ({
        ts: e.ts,
        xp: e.xp,
        problemId: e.problemId,
        title: findProblemById(e.problemId)?.title ?? e.problemId,
      }));
  }, [xpEvents]);

  const realmsVisited = useMemo(
    () => realmStats.filter(r => r.solved > 0).length,
    [realmStats],
  );

  const trophyInputs: TrophyInputs = useMemo(() => ({
    solvedCount,
    streak,
    solvedByTier,
    solvedByTopic,
    solvedSet,
    xpEvents,
    realmsVisited,
    friendCount,
  }), [solvedCount, streak, solvedByTier, solvedByTopic, solvedSet, xpEvents, realmsVisited, friendCount]);

  const trophyEarned = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const t of TROPHIES) out[t.name] = evaluateTrophyEarned(t.name, trophyInputs, realmStats);
    return out;
  }, [trophyInputs, realmStats]);

  return (
    <>
      <ProfileBody
        username={username}
        level={level}
        view="self"
        streak={streak}
        solvedCount={solvedCount}
        solvedByTier={solvedByTier}
        solvedByTopic={solvedByTopic}
        xp={xp}
        xpToNextLevel={xpToNextLevel}
        xpPercent={xpPercent}
        coins={coins}
        heatDays={heatDays}
        sparkDays={sparkDays}
        recentSolves={recentSolves}
        trophyEarned={trophyEarned}
      />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 16px 32px' }}>
        <AccountManagement handle={username} />
      </div>
    </>
  );
};

export default ProfilePage;
